import type { ReactNode } from "react";
import { ChevronDown } from "lucide-react";

import { IconButton } from "@/components/ui/icon-button";
import { cn } from "@/lib/utils";

export interface ResultsPanelHeaderProps {
  /** Panel name, e.g. "Tag Results" or "Status Progress". */
  title: string;
  /** Optional one-line explanation under the title. */
  description?: ReactNode;
  /** Pickers that scope the rows (tag/column selects), rendered next to the title. */
  children?: ReactNode;
  /** Right-aligned controls (filters, toggles, panel actions). The hide control always ends the row. */
  tools?: ReactNode;
  onHide: () => void;
  className?: string;
}

/**
 * Header row for the viewers' resizable bottom results panel (TakeoffPilot "Tag Results",
 * FieldPilot "Status Progress"): title block + scoping pickers on the left, tools and the
 * "Hide results" control on the right.
 */
export function ResultsPanelHeader({
  title,
  description,
  children,
  tools,
  onHide,
  className,
}: ResultsPanelHeaderProps) {
  return (
    <div
      data-slot="results-panel-header"
      className={cn(
        "flex flex-wrap items-center justify-between gap-x-6 gap-y-2 border-b border-border px-3 pb-2",
        className
      )}
    >
      <div className="flex min-w-0 flex-wrap items-center gap-3">
        <div className="min-w-0 shrink-0">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {title}
          </h2>
          {description ? <p className="text-xs text-muted-foreground">{description}</p> : null}
        </div>
        {children}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {tools}
        <IconButton label="Hide results" icon={ChevronDown} tooltipSide="top" onClick={onHide} />
      </div>
    </div>
  );
}

/*
 * Usage Example:
 *
 * <ResizableBottomPanel
 *   header={
 *     <ResultsPanelHeader
 *       title="Tag Results"
 *       tools={<CostsToggle … />}
 *       onHide={() => setIsPanelOpen(false)}
 *     >
 *       <MultiSelect triggerLabel="Tags" … />
 *     </ResultsPanelHeader>
 *   }
 * >
 *   …
 * </ResizableBottomPanel>
 */
